import { createSlice } from '@reduxjs/toolkit';
import { type DashboardSquares, type HealthScore } from 'types/squares.type';
import { type DownTimeDataItem } from './PartsRecommendationsState.slice';

export interface AssetComponentCostInterface {
  asset_id: string;
  timestamp: number;
  work_order_number: string;
  component: string;
  total_cost: number;
  total_labour_cost: number;
  total_part_cost: number;
}

export interface SquaresAssetCostDataInterface {
  asset: string;
  total_asset_cost: number;
}

export interface SquaresComponentCostDataInterface {
  total_cost: number;
  total_work_orders: string;
  total_hours: string;
  assets: SquaresAssetCostDataInterface[];
}

export interface DownTimeData {
  current: DownTimeDataItem[];
  previous: DownTimeDataItem[];
}

export interface DashboardSquaresState {
  squares: DashboardSquares[];
  squareAssets: DashboardSquares[];
  dashboard: {
    assets: any;
    components?: Record<string, SquaresComponentCostDataInterface>;
    timeseries?: any;
    assetComponentsCosts: Record<string, AssetComponentCostInterface[]>;
    workOrders: any[];
  };
  assetDashboard: {
    assets: any;
    components?: any;
    timeseries?: any;
  };
  healthScoreData: HealthScore[];
  maintenanceMetrics: any;
  downTimeData: DownTimeData;
  savings: {
    downtimeSaved: number;
    costSaved: number;
    productivityImproved: number;
  };
}

const initialState: DashboardSquaresState = {
  squares: [],
  squareAssets: [],
  dashboard: {
    assets: {},
    components: {},
    timeseries: {},
    assetComponentsCosts: {},
    workOrders: [],
  },
  assetDashboard: {
    assets: {},
    components: {},
    timeseries: {},
  },
  healthScoreData: [],
  maintenanceMetrics: {
    actionsCounter: {},
    workOrderStatusCounter: {},
    maintenanceCounter: {},
  },
  downTimeData: {
    current: [],
    previous: [],
  },
  savings: {
    costSaved: 0,
    downtimeSaved: 0,
    productivityImproved: 0,
  },
};

export const squaresDashboardSlice = createSlice({
  name: 'squares',
  initialState,
  reducers: {
    appendSquares(state, { payload }) {
      state.squares.push(payload);
    },

    updateSquaresList(state, { payload }: { payload: any }) {
      const index = state.squares.findIndex(
        (squares) => squares.category === payload.squaresId
      );

      if (index === -1) {
        // do not update the state if the squares is not found
        return;
      }

      state.squares[index] = payload;
    },

    setSquares(state, { payload }) {
      state.squares = payload;
    },

    setSquaresAssets(state, { payload }) {
      state.squareAssets = payload;
    },
    setSquaresDashboard(state, { payload }) {
      state.dashboard = payload;
    },
    setSquaresAssetsDashboard(state, { payload }) {
      state.assetDashboard = payload;
    },
    setHealthScore(state, { payload }) {
      state.healthScoreData = payload;
    },
    setMaintenanceMetrics(state, { payload }) {
      state.maintenanceMetrics = payload;
    },
    setDownTimeData(state, { payload }) {
      state.downTimeData = {
        previous: payload?.previousDownTimeData || [],
        current: payload?.currDownTimeData || [],
      };
    },
    setSavingsData(
      state,
      { payload }: { payload: DashboardSquaresState['savings'] }
    ) {
      state.savings = payload;
    },

    resetSquaresDashboard(state) {
      state.dashboard = {
        assets: {},
        components: {},
        timeseries: {},
        assetComponentsCosts: {},
        workOrders: [],
      };

      state.assetDashboard = {
        assets: {},
        components: {},
        timeseries: {},
      };

      state.maintenanceMetrics = {
        actionsCounter: {},
        workOrderStatusCounter: {},
        maintenanceCounter: {},
      };
    },
  },
});

export const {
  appendSquares,
  updateSquaresList,
  setSquares,
  setSquaresAssets,
  setSquaresDashboard,
  setSquaresAssetsDashboard,
  setHealthScore,
  setMaintenanceMetrics,
  setDownTimeData,
  resetSquaresDashboard,
  setSavingsData,
} = squaresDashboardSlice.actions;

export default squaresDashboardSlice.reducer;
